import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCRM } from '../../utils/CRMContext';
import { useActivitiesForEntity } from '../../hooks/useActivities';
import { Activity, ActivityType, CallOutcome, Lead } from '../../types';
import { format, isToday, isTomorrow, addDays, setHours, setMinutes } from 'date-fns';
import { 
  isGoogleCalendarConnected, 
  createGoogleEvent, 
  getDefaultCalendarForEvents 
} from '../../utils/googleCalendarService';
import {
  calculateCallMetricsFromActivities,
  getCallStatusIndicator,
  getOutcomeLabel,
  createFollowUpCallData,
  requiresFollowUp,
  getFollowUpDescription,
  getCallCadenceConfig,
} from '../../utils/callTracking';
import {
  Phone,
  PhoneOff,
  PhoneMissed,
  Voicemail,
  MessageSquare,
  Mail,
  Calendar,
  FileText,
  Home,
  Check,
  X,
  AlertTriangle,
  ClipboardList,
  ChevronRight,
  ChevronDown,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import './ActivityQuickLog.css';

interface ActivityQuickLogProps {
  entityType: 'lead' | 'deal' | 'project';
  entityId: string;
  entityName?: string;
  userId: string;
  leadCreatedAt?: Date; // For call tracking metrics
}

const ACTIVITY_CONFIG: Record<ActivityType, { icon: LucideIcon; label: string; color: string }> = {
  call: { icon: Phone, label: 'Call', color: '#4299E1' },
  text: { icon: MessageSquare, label: 'Text', color: '#48BB78' },
  email: { icon: Mail, label: 'Email', color: '#ED8936' },
  meeting: { icon: Calendar, label: 'Meeting', color: '#9F7AEA' },
  note: { icon: FileText, label: 'Note', color: '#A0AEC0' },
  'site-visit': { icon: Home, label: 'Site Visit', color: '#F56565' },
};

const ACTIVITY_TYPES: ActivityType[] = ['call', 'text', 'email', 'meeting', 'note', 'site-visit'];

// Call outcome options
const CALL_OUTCOMES: { value: CallOutcome; label: string; icon: LucideIcon; color: string }[] = [
  { value: 'answered', label: 'Answered', icon: Phone, color: '#22c55e' },
  { value: 'no_answer', label: 'No Answer', icon: PhoneMissed, color: '#ef4444' },
  { value: 'voicemail', label: 'Left Voicemail', icon: Voicemail, color: '#f59e0b' },
  { value: 'busy', label: 'Busy', icon: PhoneOff, color: '#6b7280' },
  { value: 'wrong_number', label: 'Wrong Number', icon: X, color: '#dc2626' },
  { value: 'disconnected', label: 'Disconnected', icon: PhoneOff, color: '#374151' },
];

export const ActivityQuickLog: React.FC<ActivityQuickLogProps> = ({
  entityType,
  entityId,
  entityName,
  userId,
  leadCreatedAt,
}) => {
  const { activities } = useActivitiesForEntity(entityType, entityId);
  const { createActivity, completeActivity, deleteActivity, updateLead } = useCRM();
  
  const [showScheduler, setShowScheduler] = useState(false);
  const [schedulingType, setSchedulingType] = useState<ActivityType | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleNote, setScheduleNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Call outcome state
  const [showCallOutcome, setShowCallOutcome] = useState(false);
  const [callNote, setCallNote] = useState('');
  const [completingActivityId, setCompletingActivityId] = useState<string | null>(null); // Track if completing an existing activity
  
  // Google Calendar state
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [syncToGoogle, setSyncToGoogle] = useState(false);
  
  // Check if Google Calendar is connected
  useEffect(() => {
    const checkGoogleConnection = async () => {
      if (!userId) return;
      const connected = await isGoogleCalendarConnected(userId);
      setGoogleCalendarConnected(connected);
    };
    checkGoogleConnection();
  }, [userId]);

  // Get activities for this entity
  const entityActivities = useMemo(() => {
    return activities.filter(
      a => a.entityType === entityType && a.entityId === entityId
    );
  }, [activities, entityType, entityId]);

  // Separate upcoming and completed activities
  const { upcomingActivities, completedActivities, overdueActivities } = useMemo(() => {
    const now = new Date();
    const upcoming: Activity[] = [];
    const completed: Activity[] = [];
    const overdue: Activity[] = [];

    entityActivities.forEach(activity => {
      if (activity.status === 'completed') {
        completed.push(activity);
      } else if (activity.status === 'scheduled') {
        const activityDate = activity.scheduledAt || activity.dueDate;
        if (activityDate && new Date(activityDate) < now) {
          overdue.push(activity);
        } else {
          upcoming.push(activity);
        }
      }
    });

    // Sort upcoming by date
    upcoming.sort((a, b) => {
      const dateA = a.scheduledAt || a.dueDate;
      const dateB = b.scheduledAt || b.dueDate;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    // Sort completed by date (most recent first)
    completed.sort((a, b) => {
      const dateA = a.completedAt || a.createdAt;
      const dateB = b.completedAt || b.createdAt;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    // Sort overdue by date (most overdue first)
    overdue.sort((a, b) => {
      const dateA = a.scheduledAt || a.dueDate;
      const dateB = b.scheduledAt || b.dueDate;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    return { upcomingActivities: upcoming, completedActivities: completed.slice(0, 5), overdueActivities: overdue };
  }, [entityActivities]);

  // Calculate call metrics for leads
  const callMetrics = useMemo(() => {
    if (entityType !== 'lead' || !leadCreatedAt) return null;
    return calculateCallMetricsFromActivities(
      entityActivities,
      new Date(leadCreatedAt)
    );
  }, [entityActivities, entityType, leadCreatedAt]);

  const callStatus = useMemo(() => {
    if (!callMetrics) return null;
    return getCallStatusIndicator(callMetrics);
  }, [callMetrics]);

  // Check if there's a scheduled call activity
  const hasScheduledCall = useMemo(() => {
    return entityActivities.some(
      a => a.type === 'call' && a.status === 'scheduled'
    );
  }, [entityActivities]);

  // Check if we need to show "Create Follow-up" button
  // Show when call is due/overdue but no scheduled call exists
  const showCreateFollowUp = useMemo(() => {
    if (!callStatus || !callMetrics) return false;
    const isDueOrOverdue = callStatus.status === 'due' || callStatus.status === 'overdue';
    const callCadence = getCallCadenceConfig();
    const notAtMaxAttempts = callMetrics.callAttemptNumber <= callCadence.maxAttempts;
    return isDueOrOverdue && !hasScheduledCall && notAtMaxAttempts;
  }, [callStatus, callMetrics, hasScheduledCall]);

  // Create missing follow-up call activity
  const handleCreateFollowUp = async () => {
    if (!callMetrics || !leadCreatedAt) return;
    
    setIsLoading(true);
    try {
      const attemptNumber = callMetrics.callAttemptNumber - 1; // 0-indexed
      const followUpData = createFollowUpCallData(
        entityId,
        entityName || 'Lead',
        userId,
        attemptNumber,
        new Date(leadCreatedAt)
      );
      
      if (followUpData) {
        await createActivity(followUpData);
      }
    } catch (err) {
      console.error('Error creating follow-up:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle call outcome selection
  const handleCallOutcome = async (outcome: CallOutcome) => {
    setIsLoading(true);
    try {
      if (completingActivityId) {
        // Completing an existing scheduled call activity
        await completeActivity(completingActivityId, {
          outcome: outcome,
          description: callNote || undefined,
        });
      } else {
        // Quick-logging a new call
        await createActivity({
          type: 'call',
          title: `Call - ${getOutcomeLabel(outcome)}`,
          description: callNote || undefined,
          entityType,
          entityId,
          entityName,
          createdBy: userId,
          assignedTo: userId,
          status: 'completed',
          completedAt: new Date(),
          outcome: outcome,
        });
      }

      // For leads: handle follow-up scheduling and lost status
      if (entityType === 'lead' && leadCreatedAt) {
        // Calculate updated metrics including this new call
        const updatedMetrics = calculateCallMetricsFromActivities(
          [...entityActivities, {
            id: 'temp',
            type: 'call',
            title: `Call - ${getOutcomeLabel(outcome)}`,
            entityType: 'lead',
            entityId,
            createdBy: userId,
            status: 'completed',
            completedAt: new Date(),
            outcome: outcome,
            createdAt: new Date(),
            updatedAt: new Date(),
          }],
          new Date(leadCreatedAt)
        );

        // If call was unanswered, create a follow-up activity
        if (requiresFollowUp(outcome)) {
          const currentAttempt = updatedMetrics.callAttemptNumber - 1; // 0-indexed for the function
          const callCadence = getCallCadenceConfig();
          
          if (currentAttempt < callCadence.maxAttempts) {
            // Create follow-up call activity
            const followUpData = createFollowUpCallData(
              entityId,
              entityName || 'Lead',
              userId,
              currentAttempt,
              new Date(leadCreatedAt)
            );
            
            if (followUpData) {
              await createActivity(followUpData);
              const followUpDesc = getFollowUpDescription(currentAttempt);
              console.log(`Follow-up call scheduled for ${followUpDesc}`);
            }
          }
          
          // Check if lead should be marked as lost (4+ unanswered calls)
          if (updatedMetrics.shouldMarkLost) {
            const shouldMarkLost = window.confirm(
              `This lead has had ${updatedMetrics.unansweredCalls} unanswered calls in the past week. ` +
              `Would you like to mark them as lost?`
            );
            if (shouldMarkLost) {
              await updateLead(entityId, { 
                status: 'lost',
                notes: (await getLeadNotes()) + `\n[Auto-marked lost: No response after ${updatedMetrics.unansweredCalls} call attempts]`
              });
            }
          }
        }
      }

      setShowCallOutcome(false);
      setCallNote('');
      setCompletingActivityId(null);
    } catch (err) {
      console.error('Error logging call:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to get current lead notes (placeholder - would need lead data)
  const getLeadNotes = async () => '';

  // Quick log an activity as completed
  const handleQuickLog = async (type: ActivityType) => {
    // For calls, show outcome selector instead of immediately logging
    if (type === 'call') {
      setShowCallOutcome(true);
      return;
    }

    setIsLoading(true);
    try {
      await createActivity({
        type,
        title: `${ACTIVITY_CONFIG[type].label} - ${entityName || 'Contact'}`,
        entityType,
        entityId,
        entityName,
        createdBy: userId,
        assignedTo: userId,
        status: 'completed',
        completedAt: new Date(),
      });
    } catch (err) {
      console.error('Error logging activity:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Open scheduler for a specific activity type
  const handleScheduleClick = (type: ActivityType, e: React.MouseEvent) => {
    e.preventDefault();
    setSchedulingType(type);
    setShowScheduler(true);
    // Default to tomorrow at 9am
    const tomorrow = addDays(new Date(), 1);
    setScheduleDate(format(tomorrow, 'yyyy-MM-dd'));
    setScheduleTime('09:00');
    setScheduleNote('');
  };

  // Quick schedule presets
  const handleQuickSchedule = (preset: 'today' | 'tomorrow' | 'nextWeek') => {
    const now = new Date();
    let date: Date;
    
    switch (preset) {
      case 'today':
        date = setMinutes(setHours(now, now.getHours() + 1), 0);
        break;
      case 'tomorrow':
        date = setMinutes(setHours(addDays(now, 1), 9), 0);
        break;
      case 'nextWeek':
        date = setMinutes(setHours(addDays(now, 7), 9), 0);
        break;
    }
    
    setScheduleDate(format(date, 'yyyy-MM-dd'));
    setScheduleTime(format(date, 'HH:mm'));
  };

  // Submit scheduled activity
  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedulingType || !scheduleDate) return;

    setIsLoading(true);
    try {
      const [hours, minutes] = scheduleTime.split(':').map(Number);
      // Parse date parts to create a local date (not UTC)
      const [year, month, day] = scheduleDate.split('-').map(Number);
      const scheduledAt = new Date(year, month - 1, day, hours, minutes, 0, 0);

      const activityTitle = scheduleNote || `${ACTIVITY_CONFIG[schedulingType].label} - ${entityName || 'Contact'}`;
      
      // Create the activity
      const activityData: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'> = {
        type: schedulingType,
        title: activityTitle,
        entityType,
        entityId,
        entityName,
        scheduledAt,
        createdBy: userId,
        assignedTo: userId,
        status: 'scheduled',
        syncToGoogle: syncToGoogle && googleCalendarConnected,
      };
      
      // If syncing to Google Calendar, create the Google event first
      if (syncToGoogle && googleCalendarConnected) {
        try {
          const calendarId = await getDefaultCalendarForEvents(userId);
          const googleEventId = await createGoogleEvent(userId, calendarId, activityData as Activity);
          activityData.googleEventId = googleEventId;
          activityData.googleCalendarId = calendarId;
        } catch (googleErr) {
          console.error('Error creating Google Calendar event:', googleErr);
          // Continue with activity creation even if Google sync fails
        }
      }

      await createActivity(activityData);

      setShowScheduler(false);
      setSchedulingType(null);
      setScheduleNote('');
      setSyncToGoogle(false);
    } catch (err) {
      console.error('Error scheduling activity:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark activity as complete
  const handleComplete = async (activity: Activity) => {
    if (!activity.id) return;
    
    // For call activities, show the outcome modal
    if (activity.type === 'call') {
      setCompletingActivityId(activity.id);
      setShowCallOutcome(true);
      return;
    }
    
    // For other activities, complete immediately
    setIsLoading(true);
    try {
      await completeActivity(activity.id);
    } catch (err) {
      console.error('Error completing activity:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete activity
  const handleDelete = async (activity: Activity) => {
    if (!activity.id) return;
    if (!window.confirm('Delete this activity?')) return;
    
    setIsLoading(true);
    try {
      await deleteActivity(activity.id);
    } catch (err) {
      console.error('Error deleting activity:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatActivityDate = (date: Date | undefined) => {
    if (!date) return '';
    const d = new Date(date);
    if (isToday(d)) {
      return `Today @ ${format(d, 'h:mm a')}`;
    }
    if (isTomorrow(d)) {
      return `Tomorrow @ ${format(d, 'h:mm a')}`;
    }
    return format(d, 'MMM d @ h:mm a');
  };

  return (
    <div className="activity-quick-log">
      {/* Call Status Indicator (for leads) */}
      {callStatus && entityType === 'lead' && (
        <div className="call-status-indicator" style={{ borderColor: callStatus.color }}>
          <div className="call-status-header">
            <Clock size={14} style={{ color: callStatus.color }} />
            <span className="call-status-label" style={{ color: callStatus.color }}>
              {callStatus.label}
            </span>
          </div>
          {callMetrics && callMetrics.totalCalls > 0 && (
            <div className="call-status-details">
              <span>{callMetrics.totalCalls} total calls</span>
              <span>•</span>
              <span>{callMetrics.answeredCalls} answered</span>
              {callMetrics.callAttemptNumber <= 4 && callMetrics.answeredCalls === 0 && (
                <>
                  <span>•</span>
                  <span>Attempt {callMetrics.callAttemptNumber} of 4</span>
                </>
              )}
            </div>
          )}
          {showCreateFollowUp && (
            <button 
              className="create-followup-btn"
              onClick={handleCreateFollowUp}
              disabled={isLoading}
            >
              <Phone size={14} />
              <span>Create Follow-up Activity</span>
            </button>
          )}
        </div>
      )}

      {/* Call Outcome Modal - rendered via portal to avoid event capture issues */}
      {showCallOutcome && createPortal(
        <div className="call-outcome-overlay" onClick={() => { setShowCallOutcome(false); setCompletingActivityId(null); }}>
          <div className="call-outcome-modal" onClick={e => e.stopPropagation()}>
            <div className="call-outcome-header">
              <Phone size={20} />
              <h4>{completingActivityId ? 'Complete Call' : 'Log Call Outcome'}</h4>
              <button className="close-btn" onClick={() => { setShowCallOutcome(false); setCompletingActivityId(null); }}>
                <X size={18} />
              </button>
            </div>
            
            <div className="call-outcome-options">
              {CALL_OUTCOMES.map(option => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className="call-outcome-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCallOutcome(option.value);
                    }}
                    disabled={isLoading}
                    style={{ '--outcome-color': option.color } as React.CSSProperties}
                  >
                    <IconComponent size={20} />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>

            <input
              type="text"
              className="call-note-input"
              placeholder="Add notes (optional)"
              value={callNote}
              onChange={e => setCallNote(e.target.value)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Quick Log Buttons */}
      <div className="quick-log-section">
        <div className="quick-log-header">
          <span className="quick-log-title">Log Activity</span>
          <span className="quick-log-hint">Click to log, right-click to schedule</span>
        </div>
        <div className="quick-log-buttons">
          {ACTIVITY_TYPES.map(type => {
            const IconComponent = ACTIVITY_CONFIG[type].icon;
            return (
              <button
                key={type}
                className="quick-log-btn"
                onClick={() => handleQuickLog(type)}
                onContextMenu={(e) => handleScheduleClick(type, e)}
                disabled={isLoading}
                title={`Click: Log ${ACTIVITY_CONFIG[type].label} | Right-click: Schedule`}
                style={{ '--activity-color': ACTIVITY_CONFIG[type].color } as React.CSSProperties}
              >
                <span className="btn-icon"><IconComponent size={16} /></span>
                <span className="btn-label">{ACTIVITY_CONFIG[type].label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Schedule Button */}
      <button 
        className="schedule-new-btn"
        onClick={() => {
          setSchedulingType('call');
          setShowScheduler(true);
          const tomorrow = addDays(new Date(), 1);
          setScheduleDate(format(tomorrow, 'yyyy-MM-dd'));
          setScheduleTime('09:00');
        }}
      >
        + Schedule Activity
      </button>

      {/* Scheduler Modal */}
      {showScheduler && schedulingType && (
        <div className="scheduler-overlay" onClick={() => setShowScheduler(false)}>
          <div className="scheduler-modal" onClick={e => e.stopPropagation()}>
            <div className="scheduler-header">
              <span className="scheduler-icon">
                {(() => {
                  const IconComponent = ACTIVITY_CONFIG[schedulingType].icon;
                  return <IconComponent size={20} />;
                })()}
              </span>
              <h4>Schedule {ACTIVITY_CONFIG[schedulingType].label}</h4>
              <button className="close-btn" onClick={() => setShowScheduler(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleScheduleSubmit}>
              {/* Activity Type Selector */}
              <div className="scheduler-types">
                {ACTIVITY_TYPES.map(type => {
                  const IconComponent = ACTIVITY_CONFIG[type].icon;
                  return (
                    <button
                      key={type}
                      type="button"
                      className={`type-btn ${schedulingType === type ? 'active' : ''}`}
                      onClick={() => setSchedulingType(type)}
                      style={{ '--activity-color': ACTIVITY_CONFIG[type].color } as React.CSSProperties}
                    >
                      <IconComponent size={18} />
                    </button>
                  );
                })}
              </div>

              {/* Quick Schedule Presets */}
              <div className="quick-schedule-presets">
                <button type="button" onClick={() => handleQuickSchedule('today')}>Today</button>
                <button type="button" onClick={() => handleQuickSchedule('tomorrow')}>Tomorrow</button>
                <button type="button" onClick={() => handleQuickSchedule('nextWeek')}>Next Week</button>
              </div>

              {/* Date & Time */}
              <div className="scheduler-datetime">
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  required
                />
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={e => setScheduleTime(e.target.value)}
                  required
                />
              </div>

              {/* Note */}
              <input
                type="text"
                className="scheduler-note"
                placeholder={`Add a note (optional)`}
                value={scheduleNote}
                onChange={e => setScheduleNote(e.target.value)}
              />

              {/* Google Calendar Toggle */}
              {googleCalendarConnected && (
                <label className="google-calendar-sync-toggle">
                  <input
                    type="checkbox"
                    checked={syncToGoogle}
                    onChange={e => setSyncToGoogle(e.target.checked)}
                  />
                  <span className="google-calendar-label">
                    <Calendar size={16} />
                    Add to Google Calendar
                  </span>
                </label>
              )}

              <button type="submit" className="schedule-submit-btn" disabled={isLoading}>
                {isLoading ? 'Scheduling...' : 'Schedule Activity'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Overdue Activities */}
      {overdueActivities.length > 0 && (
        <div className="activities-section overdue">
          <div className="section-header">
            <span className="section-icon"><AlertTriangle size={16} /></span>
            <span className="section-title">Overdue ({overdueActivities.length})</span>
          </div>
          <div className="activities-list">
            {overdueActivities.map(activity => {
              const IconComponent = ACTIVITY_CONFIG[activity.type]?.icon || ClipboardList;
              return (
                <div key={activity.id} className="activity-item overdue">
                  <span className="activity-icon"><IconComponent size={16} /></span>
                  <div className="activity-content">
                    <span className="activity-title">{activity.title}</span>
                    <span className="activity-date overdue">
                      {formatActivityDate(activity.scheduledAt || activity.dueDate)}
                    </span>
                  </div>
                  <div className="activity-actions">
                    <button 
                      className="complete-btn"
                      onClick={() => handleComplete(activity)}
                      disabled={isLoading}
                      title="Mark complete"
                    >
                      <Check size={14} />
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(activity)}
                      disabled={isLoading}
                      title="Delete"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming Activities */}
      {upcomingActivities.length > 0 && (
        <div className="activities-section upcoming">
          <div className="section-header">
            <span className="section-icon"><Calendar size={16} /></span>
            <span className="section-title">Upcoming ({upcomingActivities.length})</span>
          </div>
          <div className="activities-list">
            {upcomingActivities.map(activity => {
              const IconComponent = ACTIVITY_CONFIG[activity.type]?.icon || ClipboardList;
              return (
                <div key={activity.id} className="activity-item scheduled">
                  <span className="activity-icon"><IconComponent size={16} /></span>
                  <div className="activity-content">
                    <span className="activity-title">{activity.title}</span>
                    <span className="activity-date">
                      {formatActivityDate(activity.scheduledAt || activity.dueDate)}
                    </span>
                  </div>
                  <div className="activity-actions">
                    <button 
                      className="complete-btn"
                      onClick={() => handleComplete(activity)}
                      disabled={isLoading}
                      title="Mark complete"
                    >
                      <Check size={14} />
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(activity)}
                      disabled={isLoading}
                      title="Delete"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed Activities */}
      <div className="activities-section completed">
        <button 
          className="section-header clickable"
          onClick={() => setShowCompleted(!showCompleted)}
        >
          <span className="section-icon"><Check size={16} /></span>
          <span className="section-title">
            Recent Activity ({completedActivities.length})
          </span>
          <span className="expand-icon">
            {showCompleted ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </button>
        
        {showCompleted && completedActivities.length > 0 && (
          <div className="activities-list">
            {completedActivities.map(activity => {
              const IconComponent = ACTIVITY_CONFIG[activity.type]?.icon || ClipboardList;
              return (
                <div key={activity.id} className="activity-item completed">
                  <span className="activity-icon"><IconComponent size={16} /></span>
                  <div className="activity-content">
                    <span className="activity-title">{activity.title}</span>
                    <span className="activity-date completed">
                      {formatActivityDate(activity.completedAt || activity.createdAt)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {showCompleted && completedActivities.length === 0 && (
          <div className="empty-state">No activities logged yet</div>
        )}
      </div>
    </div>
  );
};

export default ActivityQuickLog;
